import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Spinner,
  Center,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { ChevronRightIcon } from "@chakra-ui/icons";

const Notice = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchNotices();
  }, [currentPage]);

  const fetchNotices = async () => {
    try {
      setLoading(true);

      // 전체 공지사항 개수 조회
      const { count, error: countError } = await supabase
        .from("notice")
        .select("*", { count: "exact", head: true })
        .eq("is_visible", true);

      if (countError) throw countError;

      // 페이지네이션을 위한 공지사항 조회
      const startIndex = (currentPage - 1) * itemsPerPage;
      const { data, error } = await supabase
        .from("notice")
        .select("id, title, content, created_at, is_visible")
        .eq("is_visible", true)
        .order("created_at", { ascending: false })
        .range(startIndex, startIndex + itemsPerPage - 1);

      if (error) throw error;

      setNotices(data || []);
      setTotalPages(Math.ceil((count || 0) / itemsPerPage));
    } catch (error) {
      console.error("공지사항 조회 실패:", error);
      toast({
        title: "공지사항을 불러오는데 실패했습니다",
        description: error.message || "알 수 없는 오류가 발생했습니다",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNoticeClick = (noticeId) => {
    navigate(`/notice/${noticeId}`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="container.lg" py={8}>
        <VStack spacing={8} align="stretch">
          {/* 페이지 제목 */}
          <Box textAlign="center" py={8}>
            <Heading size="xl" color="blue.600" mb={4}>
              공지사항
            </Heading>
            <Text color="gray.600" fontSize="lg">
              플플의 최신 소식과 안내사항를 확인하세요
            </Text>
          </Box>

          {/* 공지사항 목록 */}
          <VStack spacing={4} align="stretch">
            {notices.length === 0 ? (
              <Center py={16}>
                <Text color="gray.500" fontSize="lg">
                  등록된 공지사항이 없습니다
                </Text>
              </Center>
            ) : (
              notices.map((notice) => (
                <Card
                  key={notice.id}
                  cursor="pointer"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                    transition: "all 0.2s",
                  }}
                  transition="all 0.2s"
                  onClick={() => handleNoticeClick(notice.id)}
                >
                  <CardHeader pb={2}>
                    <Flex justify="space-between" align="center">
                      <Heading size="md" color="gray.800" noOfLines={1}>
                        {notice.title}
                      </Heading>
                      <Badge colorScheme="blue" variant="subtle">
                        {formatDate(notice.created_at)}
                      </Badge>
                    </Flex>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.500">
                        자세히 보기
                      </Text>
                      <ChevronRightIcon color="blue.500" />
                    </Flex>
                  </CardBody>
                </Card>
              ))
            )}
          </VStack>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <Flex justify="center" mt={8}>
              <HStack spacing={2}>
                <Button
                  onClick={() => setCurrentPage(1)}
                  isDisabled={currentPage === 1}
                  size="sm"
                  variant="outline"
                >
                  처음
                </Button>
                <Button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  isDisabled={currentPage === 1}
                  size="sm"
                  variant="outline"
                >
                  이전
                </Button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (page) =>
                      page === 1 ||
                      page === totalPages ||
                      Math.abs(page - currentPage) <= 1
                  )
                  .map((page, index, array) => (
                    <React.Fragment key={page}>
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <Text color="gray.400">...</Text>
                      )}
                      <Button
                        onClick={() => setCurrentPage(page)}
                        colorScheme={currentPage === page ? "blue" : "gray"}
                        variant={currentPage === page ? "solid" : "outline"}
                        size="sm"
                      >
                        {page}
                      </Button>
                    </React.Fragment>
                  ))}

                <Button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  isDisabled={currentPage === totalPages}
                  size="sm"
                  variant="outline"
                >
                  다음
                </Button>
                <Button
                  onClick={() => setCurrentPage(totalPages)}
                  isDisabled={currentPage === totalPages}
                  size="sm"
                  variant="outline"
                >
                  마지막
                </Button>
              </HStack>
            </Flex>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default Notice;
